import {
  AutomationActionType,
  AutomationEventType,
  AutomationTriggerType,
  FilterOperator,
} from '../../../../api/automations-workflows-client/automations-workflows-client.type';
import { type ActionStepVisual, AutomationGroupKey } from './agent-automations.type';

// Work-period event labels are derived from the shared base dictionary's
// `common.work_period` term at call time (see getEventLabel), so they are
// intentionally excluded from this static map.
const EVENT_TYPE_LABELS: Record<
  Exclude<AutomationEventType, AutomationEventType.WORK_PERIOD_STARTED | AutomationEventType.WORK_PERIOD_ENDED>,
  string
> = {
  [AutomationEventType.TARGET_MISSED]: 'Target missed',
  [AutomationEventType.UNEXPECTED_TASK_EVENT]: 'Task unexpected event',
  [AutomationEventType.SKIPPED_STATUS]: 'Task skipped status',
  [AutomationEventType.TASK_CREATED]: 'Task created',
  [AutomationEventType.TASK_STATUS_CHANGED]: 'Task status changed',
  [AutomationEventType.TASK_FIELD_CHANGED]: 'Task field changed',
  [AutomationEventType.FLORA_DEBUG_ERROR]: 'Flora error',
};

const EVENT_TYPE_ICONS: Record<AutomationEventType, string> = {
  [AutomationEventType.TARGET_MISSED]: 'crisis_alert',
  [AutomationEventType.UNEXPECTED_TASK_EVENT]: 'event_busy',
  [AutomationEventType.SKIPPED_STATUS]: 'skip_next',
  [AutomationEventType.TASK_CREATED]: 'add_task',
  [AutomationEventType.TASK_STATUS_CHANGED]: 'sync_alt',
  [AutomationEventType.TASK_FIELD_CHANGED]: 'edit',
  [AutomationEventType.WORK_PERIOD_STARTED]: 'play_circle',
  [AutomationEventType.WORK_PERIOD_ENDED]: 'flag',
  [AutomationEventType.FLORA_DEBUG_ERROR]: 'error',
};

const ACTION_TYPE_LABELS: Record<AutomationActionType, string> = {
  [AutomationActionType.FLORA_PROMPT]: 'Ask Flora',
  [AutomationActionType.STATIC_MESSAGE]: 'Static message',
};

const OPERATOR_LABELS: Record<string, string> = {
  [FilterOperator.IS]: 'is',
  [FilterOperator.IS_NOT]: 'is not',
  [FilterOperator.IS_ANY_OF]: 'is any of',
  [FilterOperator.IS_NONE_OF]: 'is none of',
  [FilterOperator.EQUALS]: 'equals',
  [FilterOperator.CONTAINS]: 'contains',
  [FilterOperator.GT]: 'greater than',
  [FilterOperator.GTE]: 'greater than or equal',
  [FilterOperator.LT]: 'less than',
  [FilterOperator.LTE]: 'less than or equal',
  [FilterOperator.BETWEEN]: 'between',
  [FilterOperator.BEFORE]: 'before',
  [FilterOperator.AFTER]: 'after',
  [FilterOperator.IS_TRUE]: 'is true',
  [FilterOperator.IS_FALSE]: 'is false',
};

const MULTI_VALUE_OPERATORS = new Set<string>([FilterOperator.IS_ANY_OF, FilterOperator.IS_NONE_OF]);
const BOOLEAN_VALUE_OPERATORS = new Set<string>([FilterOperator.IS_TRUE, FilterOperator.IS_FALSE]);

type FilterOptionItem = { value: string; label: string };

// Task-trigger filter options. Each `value` must exactly match (lowercase) what
// the BE emits into `trigger_data`, since filters match by string equality.
const PRIORITY_OPTIONS: FilterOptionItem[] = [
  { value: 'highest', label: 'Highest' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
  { value: 'lowest', label: 'Lowest' },
  { value: 'unknown', label: 'Unknown' },
];

const TASK_SECTION_OPTIONS: FilterOptionItem[] = [
  { value: 'backlog', label: 'Backlog' },
  { value: 'blocked', label: 'Blocked' },
  { value: 'wip', label: 'In Progress' },
  { value: 'review', label: 'Review' },
  { value: 'test', label: 'Test' },
  { value: 'deployable', label: 'Deployable' },
  { value: 'done', label: 'Done' },
];

const TASK_DIRECTION_OPTIONS: FilterOptionItem[] = [
  { value: 'forward', label: 'Forward' },
  { value: 'backward', label: 'Backward' },
  { value: 'lateral', label: 'Lateral' },
];

const VALUE_DIRECTION_OPTIONS: FilterOptionItem[] = [
  { value: 'increased', label: 'Increased' },
  { value: 'decreased', label: 'Decreased' },
];

const TASK_FIELD_TYPE_OPTIONS: FilterOptionItem[] = [
  { value: 'assignee', label: 'Assignee' },
  { value: 'points', label: 'Points' },
  { value: 'priority', label: 'Priority' },
  { value: 'sprint', label: 'Sprint' },
  { value: 'due_date', label: 'Due date' },
  { value: 'epic', label: 'Epic' },
];

// Normalized values, not raw integration issue-type strings — matching "Epic" would never fire.
const TASK_TYPE_OPTIONS: FilterOptionItem[] = [
  { value: 'bug', label: 'Bug' },
  { value: 'defect', label: 'Defect' },
  { value: 'card', label: 'Card' },
  { value: 'custom', label: 'Custom' },
  { value: 'epic', label: 'Epic' },
  { value: 'spike', label: 'Spike' },
  { value: 'story', label: 'Story' },
  { value: 'sub-task', label: 'Sub-task' },
  { value: 'task', label: 'Task' },
  { value: 'unknown', label: 'Unknown' },
  { value: 'user story', label: 'User story' },
  { value: 'product backlog item', label: 'Product backlog item' },
  { value: 'feature', label: 'Feature' },
  { value: 'issue', label: 'Issue' },
  { value: 'test case', label: 'Test case' },
  { value: 'test suite', label: 'Test suite' },
  { value: 'test plan', label: 'Test plan' },
  { value: 'impediment', label: 'Impediment' },
  { value: 'initiative', label: 'Initiative' },
];

// Rule-card header icons keyed by the BE's `options_source`; sources without an
// entry (including free-text `null` sources) fall back to DEFAULT_FILTER_FIELD_ICON.
const FILTER_FIELD_ICONS: Record<string, string> = {
  targets: 'track_changes',
  measures: 'straighten',
  projects: 'folder',
  boards: 'view_kanban',
  portfolios: 'business_center',
  initiatives: 'flag',
  workflows: 'account_tree',
  workflow_statuses: 'label',
  task_event_types: 'event',
  task_event_relationships: 'link',
  flora_feedback_sentiments: 'sentiment_satisfied',
  priorities: 'priority_high',
  task_sections: 'view_column',
  task_directions: 'swap_horiz',
  value_directions: 'swap_vert',
  task_field_types: 'edit',
  task_types: 'category',
};

const DEFAULT_FILTER_FIELD_ICON = 'filter_alt';

// Icons are the one preset attribute the FE owns; unknown keys fall back to DEFAULT_PRESET_ICON.
const PRESET_ICONS: Record<string, string> = {
  task_completed: 'check_circle',
  task_started: 'play_arrow',
  task_moved_backwards: 'undo',
  task_reopened: 'replay',
  points_changed: 'exposure',
  points_increased: 'trending_up',
  assignee_changed: 'person',
  points_or_assignee_changed: 'sync',
  sprint_changed: 'bolt',
  priority_changed: 'flag',
  priority_escalated: 'trending_up',
};

const DEFAULT_PRESET_ICON = 'bolt';

const TRIGGER_TYPE_TO_GROUP_KEY: Record<AutomationTriggerType, AutomationGroupKey> = {
  [AutomationTriggerType.EVENT]: AutomationGroupKey.EVENT,
  [AutomationTriggerType.CRON]: AutomationGroupKey.CRON,
  [AutomationTriggerType.MANUAL]: AutomationGroupKey.MANUAL,
};

const GROUP_ORDER: AutomationGroupKey[] = [
  AutomationGroupKey.EVENT,
  AutomationGroupKey.CRON,
  AutomationGroupKey.MANUAL,
];

const ACTION_STEP_VISUALS: Record<AutomationActionType, ActionStepVisual> = {
  [AutomationActionType.FLORA_PROMPT]: {
    icon: 'psychology',
    iconColor: 'var(--secondary-base)',
    iconBackground: 'rgba(87, 92, 229, 0.12)',
  },
  [AutomationActionType.STATIC_MESSAGE]: {
    icon: 'format_quote',
    iconColor: 'var(--primary-base)',
    iconBackground: 'rgba(35, 193, 107, 0.12)',
  },
};

const TRIGGER_STEP_VISUAL: ActionStepVisual = {
  icon: 'bolt',
  iconColor: 'var(--amber-base, #d98e2b)',
  iconBackground: 'rgba(217, 142, 43, 0.14)',
};

const DELIVERY_STEP_VISUAL: ActionStepVisual = {
  icon: 'send',
  iconColor: 'var(--blue-base, #2563eb)',
  iconBackground: 'rgba(37, 99, 235, 0.12)',
};

const SCHEDULE_TRIGGER_VISUAL: ActionStepVisual = {
  icon: 'schedule',
  iconColor: 'var(--secondary-base)',
  iconBackground: 'rgba(87, 92, 229, 0.1)',
};

const MANUAL_TRIGGER_VISUAL: ActionStepVisual = {
  icon: 'pan_tool',
  iconColor: 'var(--ink-dark)',
  iconBackground: 'var(--sky-lighter)',
};

export {
  ACTION_STEP_VISUALS,
  ACTION_TYPE_LABELS,
  BOOLEAN_VALUE_OPERATORS,
  DEFAULT_FILTER_FIELD_ICON,
  DEFAULT_PRESET_ICON,
  DELIVERY_STEP_VISUAL,
  EVENT_TYPE_ICONS,
  EVENT_TYPE_LABELS,
  FILTER_FIELD_ICONS,
  GROUP_ORDER,
  MANUAL_TRIGGER_VISUAL,
  MULTI_VALUE_OPERATORS,
  OPERATOR_LABELS,
  PRESET_ICONS,
  PRIORITY_OPTIONS,
  SCHEDULE_TRIGGER_VISUAL,
  TASK_DIRECTION_OPTIONS,
  TASK_FIELD_TYPE_OPTIONS,
  TASK_SECTION_OPTIONS,
  TASK_TYPE_OPTIONS,
  TRIGGER_STEP_VISUAL,
  TRIGGER_TYPE_TO_GROUP_KEY,
  VALUE_DIRECTION_OPTIONS,
};
